/**
 * The booking column's behavior half: reads the service (or event), the shown
 * month's open days and the chosen day's open times, all in the visitor's own
 * zone, keeps the pick, and carries it to the checkout address. Draws nothing.
 *
 * An address carrying `?start=<UTC instant>` (the home's Open times links)
 * preselects that day and time while it is still open.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { SiteOpenTime } from '@telify/shared';

import { useWebSession } from '../../api/session';
import { useAvailableDates, useDayTimes, useSiteEvent, useSiteService } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone, zoneName } from '../../format';
import { isViewableBooking } from '@/utils/bookingStatus';

/** Noon UTC of a YYYY-MM-DD day: inside that day in every zone a visitor may be in, for naming its zone. */
const dayRef = (day: string) => new Date(`${day}T12:00:00Z`);
import { todayYmd, ymdFromIso } from '@/pages/SessionDetailPage/detailFormat';
import { usePageMover } from '../../pageMover';
import { contract, type BookingColumnActions, type BookingColumnEvents, type BookingColumnState } from './contract';

export type BookingColumnRuntime = PieceRuntime<typeof contract, BookingColumnState, BookingColumnActions>;

export interface BookingColumnInput extends PieceInput<BookingColumnEvents> {
  /** The service to book; defaults to the page's service. */
  readonly serviceId?: string;
  /** The event to book; defaults to the page's event. */
  readonly eventId?: string;
}

/** How many empty months the calendar moves past on its own before it waits for the visitor. */
const AUTO_ADVANCE = 2;

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The last day of a YYYY-MM month, YYYY-MM-DD. */
function lastDay(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** The month after a YYYY-MM month. */
export function nextMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** A UTC instant from the address, or null when it is not one. */
function readStart(raw: string | null): string | null {
  if (!raw) return null;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

const sameInstant = (a: string, b: string) => new Date(a).getTime() === new Date(b).getTime();

export function useBookingColumn(input: BookingColumnInput = {}): BookingColumnRuntime {
  const options = usePieceOptions(contract, input.options);
  const session = useWebSession().status;
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const params = useParams<{ serviceId?: string; eventId?: string }>();
  const [search] = useSearchParams();
  // web-studio-sites D14: placed on a custom page, the service comes from the `service` option.
  const placedService = options.service ?? undefined;
  const explicit = Boolean(input.serviceId || input.eventId || placedService);
  const serviceId = explicit ? (input.serviceId ?? placedService) : params.serviceId;
  const eventId = serviceId ? undefined : explicit ? input.eventId : params.eventId;
  const kind: BookingColumnState['kind'] = serviceId ? 'service' : 'event';

  const timezone = useMemo(() => getClientTimezone(), []);
  const today = todayYmd(timezone);

  const serviceQuery = useSiteService(serviceId);
  const eventQuery = useSiteEvent(eventId);
  const service = serviceQuery.data ?? null;
  const event = eventQuery.data ?? null;
  const paused = service ? !service.acceptingBookings : false;

  // The address's preselected time, used once, only when it is not in the past.
  const startParam = useRef(readStart(search.get('start')));
  const startDay = startParam.current ? ymdFromIso(startParam.current, timezone) : null;
  const [month, setMonth] = useState(() => (startDay && startDay >= today ? startDay : today).slice(0, 7));
  const moved = useRef(false);
  const advances = useRef(0);

  const from = month === today.slice(0, 7) ? today : `${month}-01`;
  const datesQuery = useAvailableDates(kind === 'service' && !paused ? serviceId : undefined, from, lastDay(month), timezone);
  const datesReady = datesQuery.isSuccess && !datesQuery.isPlaceholderData;
  const openDays = useMemo(
    () => (datesQuery.data?.dates ?? []).filter((d) => d.startsWith(month) && d >= today),
    [datesQuery.data, month, today],
  );

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);

  // Once the shown month's open days land: an empty first month moves on by itself (a little), and the
  // first open day (or the address's day, while still open) is chosen when nothing is.
  useEffect(() => {
    if (!datesReady) return;
    if (openDays.length === 0) {
      if (!moved.current && selectedDay === null && advances.current < AUTO_ADVANCE) {
        advances.current += 1;
        setMonth(nextMonth(month));
      }
      return;
    }
    if (selectedDay !== null) return;
    setSelectedDay(startDay && openDays.includes(startDay) ? startDay : openDays[0]);
  }, [datesReady, openDays, month, selectedDay, startDay]);

  const timesQuery = useDayTimes(kind === 'service' && !paused ? serviceId : undefined, selectedDay, timezone);
  const times = useMemo<readonly SiteOpenTime[]>(
    () => (timesQuery.data && timesQuery.data.date === selectedDay ? timesQuery.data.times : []),
    [timesQuery.data, selectedDay],
  );

  // The address's time, once its day's times land and it is still open.
  useEffect(() => {
    const start = startParam.current;
    if (!start || selectedDay !== startDay || !timesQuery.isSuccess) return;
    startParam.current = null;
    const open = times.find((t) => sameInstant(t.startsAt, start));
    if (open) setSelectedStart(open.startsAt);
  }, [times, selectedDay, startDay, timesQuery.isSuccess]);

  const chosen = times.find((t) => t.startsAt === selectedStart) ?? null;

  // The client's own booking comes first, as the app's event button does (View booking, then Sold out, then closed):
  // with it, Continue would only start a second checkout the server refuses (Codex review 2026-10-26).
  const booked = event && isViewableBooking(event.userBooking) ? event.userBooking : null;
  const eventClosed: BookingColumnState['eventClosed'] = !event
    ? null
    : event.spotsLeft <= 0
      ? 'full'
      : event.registrationClosed || !event.isBookable
        ? 'closed'
        : null;

  const canContinue = kind === 'service' ? Boolean(service && !paused && chosen) : Boolean(event && !eventClosed && !booked);

  const actions = useMemo<BookingColumnActions>(
    () => ({
      showMonth: (next) => {
        if (!MONTH.test(next) || next < today.slice(0, 7)) return;
        moved.current = true;
        setMonth(next);
      },
      pickDay: (day) => {
        if (!openDays.includes(day)) return;
        setSelectedDay(day);
        setSelectedStart(null);
      },
      pickTime: (startsAt) => {
        if (!serviceId || !times.some((t) => t.startsAt === startsAt)) return;
        setSelectedStart(startsAt);
        emit('picked', { serviceId, startsAt });
      },
      continue: () => {
        if (!canContinue) return;
        if (kind === 'event' && event) {
          emit('eventContinued', { eventId: event.id });
          navigate(`/checkout?${new URLSearchParams({ event: event.id })}`);
          return;
        }
        if (!service || !chosen) return;
        emit('continued', { serviceId: service.id, startsAt: chosen.startsAt });
        navigate(`/checkout?${new URLSearchParams({ service: service.id, start: chosen.startsAt })}`);
      },
      viewBooking: () => {
        if (booked) navigate(`/account/bookings/${encodeURIComponent(booked.id)}`);
      },
    }),
    [today, openDays, serviceId, times, emit, canContinue, kind, event, service, chosen, navigate, booked],
  );

  const offeringQuery = kind === 'service' ? serviceQuery : eventQuery;
  const status: BookingColumnState['status'] =
    offeringQuery.isError || datesQuery.isError
      ? 'error'
      : offeringQuery.isPending || (kind === 'service' && !paused && datesQuery.isPending)
        ? 'loading'
        : 'ready';
  const timesStatus: BookingColumnState['timesStatus'] =
    kind === 'event' || !selectedDay
      ? 'idle'
      : timesQuery.isError
        ? 'error'
        : timesQuery.isPending || timesQuery.data?.date !== selectedDay
          ? 'loading'
          : 'ready';

  return {
    state: {
      status,
      service,
      timezone,
      // The zone as it is on the day in view (a later day may be past a clock change).
      zoneName: zoneName(
        timezone,
        event ? new Date(event.startTime) : chosen ? new Date(chosen.startsAt) : selectedDay ? dayRef(selectedDay) : undefined,
      ),
      month,
      openDays,
      selectedDay: kind === 'event' && event ? ymdFromIso(event.startTime, timezone) : selectedDay,
      timesStatus,
      times,
      selectedStart: kind === 'event' ? (event?.startTime ?? null) : (chosen?.startsAt ?? null),
      canContinue,
      kind,
      event,
      today,
      paused,
      eventClosed,
      booked,
      signedIn: session === 'signedIn',
    },
    actions,
    options,
  };
}
